<template>
	<div v-show="show" class="row mb-4 w-[calc(100%+0.5rem)] pl-2">
		<div class="flex w-full flex-wrap items-end gap-2">
			<div
				v-if="showTenantFilter && (tenants?.length > 1 || $global.hasUserRole(userRoles, 'reseller'))"
				class="w-[calc(50%-0.25rem)] lg:w-[calc(25%-0.375rem)]"
			>
				<tenant-select
					:model-value="filters.selectedTenant"
					:tenants="tenants"
					:all-tenants-label="allTenantsLabel"
					@update:model-value="handleTenantChange"
				/>
			</div>

			<div
				v-if="showAccountFilter && filteredAccounts?.length > 0 && tenants?.length"
				class="w-[calc(50%-0.25rem)] lg:w-[calc(25%-0.375rem)]"
			>
				<account-select
					:model-value="filters.selectedAccount"
					:accounts="filteredAccounts"
					:allow-tenants="allowTenants"
					:tenant-label="tenantLabel"
					@update:model-value="handleAccountChange"
				/>
			</div>

			<div v-if="showDateFilter" class="w-[calc(50%-0.25rem)] lg:w-[calc(25%-0.375rem)]">
				<date-range-picker
					:model-value="dates"
					:label="dateLabel"
					:max-range="maxDateRange"
					@update:model-value="handleDateChange"
					@clear-dates="clearDates"
				/>
			</div>

			<div v-if="showSearchFilter" class="w-[calc(50%-0.25rem)] lg:w-[calc(25%-0.375rem)]">
				<search-field :model-value="searchTerm" :label="searchLabel" @update:model-value="handleSearchChange" />
			</div>

			<div v-if="showSecondarySearchFilter" class="w-[calc(50%-0.25rem)] lg:w-[calc(25%-0.375rem)]">
				<search-field
					:model-value="secondarySearchTerm"
					:label="secondarySearchLabel"
					@update:model-value="handleSecondarySearchChange"
				/>
			</div>

			<div v-if="showStatusFilter" class="w-[calc(50%-0.25rem)] lg:w-[calc(25%-0.375rem)]">
				<status-select :model-value="filters.status" @update:model-value="handleStatusChange" />
			</div>

			<slot></slot>
		</div>

		<div class="flex w-full items-center justify-center pt-4">
			<active-filters
				:filters="filters"
				:search-term="searchTerm"
				:search-label="searchLabel"
				:selected-tenant-name="selectedTenantName"
				:selected-account-name="selectedAccountName"
				:accounts="accounts"
				@update:filters="$emit('update:filters', $event)"
				@update:search-term="$emit('update:searchTerm', $event)"
				@update:secondary-search-term="$emit('update:secondarySearchTerm', $event)"
				@tenant-cleared="handleTenantChange('')"
				@account-cleared="handleAccountChange(null)"
				@dates-cleared="clearDates"
			/>
		</div>
	</div>
</template>

<script>
import moment from 'moment'

export default {
	inject: ['userRoles'],
	props: {
		show: Boolean,
		filters: {
			type: Object,
			required: true
		},
		searchTerm: String,
		secondarySearchTerm: String,
		tenants: Array,
		accounts: Array,
		allowTenants: Boolean,
		tenantLabel: String,
		dateLabel: String,
		searchLabel: String,
		secondarySearchLabel: String,
		showTenantFilter: Boolean,
		showAccountFilter: Boolean,
		showDateFilter: Boolean,
		showSearchFilter: Boolean,
		showSecondarySearchFilter: Boolean,
		showStatusFilter: Boolean,
		allTenantsLabel: {
			type: String,
			default: 'All Tenants'
		},
		maxDateRange: {
			type: Number,
			default: 730 // Default to 2 years
		}
	},
	emits: ['update:filters', 'update:searchTerm', 'update:secondarySearchTerm', 'tenant-change', 'account-change'],
	data() {
		return {
			dates: []
		}
	},
	computed: {
		filteredAccounts() {
			return (this.accounts || [])
				.filter(
					(account) =>
						!this.filters.selectedTenant || account.tenant?.id === parseInt(this.filters.selectedTenant)
				)
				.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
		},
		selectedTenantName() {
			if (!this.filters.selectedTenant) return ''
			const tenant = this.tenants?.find((t) => t.id === parseInt(this.filters.selectedTenant))
			return tenant?.companyName || ''
		},
		selectedAccountName() {
			if (this.filters.selectedAccount === '-1') return this.tenantLabel
			if (!this.filters.selectedAccount) return ''
			const account = this.accounts?.find((a) => a.id === parseInt(this.filters.selectedAccount))
			return account?.name || ''
		}
	},
	created() {
		if (this.tenants?.length === 1) {
			const tenant = this.tenants[0]
			this.$emit('update:filters', {
				...this.filters,
				selectedTenant: tenant.id
			})
			this.$emit('tenant-change', { target: { value: tenant.id } })
		} else {
			const storedTenant = this.$global.getSelectedTenant()
			if (storedTenant) {
				const tenant = this.tenants?.find((t) => t.id === parseInt(storedTenant))
				if (tenant) {
					this.$emit('update:filters', {
						...this.filters,
						selectedTenant: storedTenant
					})
					this.$emit('tenant-change', { target: { value: storedTenant } })
				}
			}
		}
	},
	methods: {
		handleTenantChange(value) {
			this.$emit('update:filters', {
				...this.filters,
				selectedTenant: value,
				selectedAccount: null
			})
			this.$emit('tenant-change', { target: { value } })
		},
		handleAccountChange(value) {
			this.$emit('update:filters', {
				...this.filters,
				selectedAccount: value
			})
			this.$emit('account-change', value)
		},
		handleSearchChange(value) {
			this.$emit('update:searchTerm', value)
		},
		handleSecondarySearchChange(value) {
			this.$emit('update:secondarySearchTerm', value)
		},
		handleDateChange(value) {
			this.dates = value
			if (this.dates?.length === 2) {
				this.$emit('update:filters', {
					...this.filters,
					dateAddedFrom: moment(this.dates[0]).format('YYYY-MM-DD'),
					dateAddedTo: moment(this.dates[1]).format('YYYY-MM-DD')
				})
			}
		},
		handleStatusChange(value) {
			this.$emit('update:filters', {
				...this.filters,
				status: value
			})
		},
		clearDates() {
			this.dates = []
			this.$emit('update:filters', {
				...this.filters,
				dateAddedFrom: null,
				dateAddedTo: null
			})
		}
	}
}
</script>
